import { isLanguage, translations } from './translations';
import type {
  ConsentWidgetOptions,
  OptionalCategoryKey,
  ResolvedConsentWidgetOptions,
} from './types';

const OPTIONAL_CATEGORIES: OptionalCategoryKey[] = ['analytics', 'marketing', 'remarketing', 'email', 'preferences'];

const isLayout = (value: unknown): value is 'fluid' | 'box' => value === 'fluid' || value === 'box';
const isPosition = (value: unknown): value is 'bottom-left' | 'bottom-right' => value === 'bottom-left' || value === 'bottom-right';

const normalizeCategories = (value?: OptionalCategoryKey[]): OptionalCategoryKey[] => {
  const requested = value?.filter((category): category is OptionalCategoryKey => OPTIONAL_CATEGORIES.includes(category))
    ?? ['analytics', 'marketing'];

  return [...new Set(requested)];
};

export const parseScriptOptions = (script: HTMLScriptElement): ConsentWidgetOptions => {
  const { dataset } = script;
  const waitForUpdate = Number.parseInt(dataset.waitForUpdate ?? '', 10);

  return {
    gtmId: dataset.gtmId,
    siteId: dataset.site,
    accent: dataset.accent,
    accentText: dataset.accentText,
    layout: isLayout(dataset.layout) ? dataset.layout : undefined,
    position: isPosition(dataset.position) ? dataset.position : undefined,
    language: isLanguage(dataset.language) ? dataset.language : undefined,
    privacyUrl: dataset.privacyUrl,
    storageKey: dataset.storageKey,
    consentVersion: dataset.consentVersion,
    categories: dataset.categories
      ?.split(',')
      .map((category) => category.trim())
      .filter((category): category is OptionalCategoryKey => OPTIONAL_CATEGORIES.includes(category as OptionalCategoryKey)),
    title: dataset.title,
    description: dataset.description,
    waitForUpdate: Number.isFinite(waitForUpdate) ? waitForUpdate : undefined,
    debug: dataset.debug === 'true',
    nonce: script.nonce || dataset.nonce,
  };
};

export const resolveOptions = (options: ConsentWidgetOptions = {}): ResolvedConsentWidgetOptions => {
  const language = isLanguage(options.language) ? options.language : 'cs';
  const baseTexts = translations[language];
  const siteId = options.siteId?.trim() ?? '';
  const waitForUpdate = Math.min(Math.max(options.waitForUpdate ?? 500, 0), 10_000);

  return {
    gtmId: options.gtmId?.trim() ?? '',
    siteId,
    accent: options.accent?.trim() || '#69ac27',
    accentText: options.accentText?.trim() || '#ffffff',
    layout: isLayout(options.layout) ? options.layout : 'fluid',
    position: isPosition(options.position) ? options.position : 'bottom-right',
    language,
    privacyUrl: options.privacyUrl?.trim() ?? '/gdpr',
    storageKey: options.storageKey?.trim() || (siteId ? `gd_consent:${siteId}` : 'gd_consent'),
    consentVersion: options.consentVersion?.trim() || '1',
    categories: normalizeCategories(options.categories),
    texts: {
      ...baseTexts,
      bannerTitle: options.title?.trim() || baseTexts.bannerTitle,
      bannerDescription: options.description?.trim() || baseTexts.bannerDescription,
    },
    waitForUpdate,
    debug: options.debug ?? false,
    nonce: options.nonce?.trim() ?? '',
  };
};
